import { Checkbox, Divider, ICONS } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterTypeEnum } from '@creditsights/api-ui-web';
import { EXTERNAL_ROUTES, MY_CONTENT_ROUTES, ROUTES } from '../../../Base.constants';
import FiltersBar from '../../../components/FiltersBar';
import { getFeedFilters, transformFilter } from '../../../components/FiltersBar/constants';
import ResultsFilter from '../../../components/ResultsFilter/ResultsFilter';
import SubHeader from '../../../components/SubHeader';
import { useBreakpoint, useDisplayType, useFeedFilters } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import { ContentTypeFilter, FeedType } from '../../../models';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import { navigateToAnotherMfe } from '../../../utils/navigationUtils';
import NoContent from '../../MyContent/NoContent/NoContent';
import ArticleList from '../ArticleList';
import { viewConfig } from '../ArticleList/ArticleList.logic';
import { ContentTabs } from './ContentTabs/ContentTabs';
import DisplayTypeSelection, { DisplayType } from './DisplayTypeSelection';
import { FEED_SECTION_PAGINATION, contentTypeFiltersMap, getSubHeaderRoutes } from './FeedLeftContainer.logic';
import styles from './FeedLeftContainer.module.scss';
import PersonalizeMyCSButton from './PersonalizeMyCSButton';

const cx = classNames.bind(styles);

interface FeedLeftContainerProps {
  feedType: FeedType;
  isNoContent: boolean;
  onPreviewableArticleClick: (article: ArticleTableRow, event: MouseEvent<HTMLElement>) => void;
  isPreviewable: boolean;
  resetPreviewArticleId: () => void;
  previewArticleId: number | null;
  subHeaderTitle?: string;
  hideSubHeaderRoutes?: boolean;
  openArticlePreviewOnClick: boolean;
  setOpenArticlePreviewOnClick: (value: boolean) => void;
  isOnboardingCompleted: boolean;
  articleTypeFilter: ContentTypeFilter;
  setArticleTypeFilter: (filter: ContentTypeFilter) => void;
}

const FeedLeftContainer = ({
  feedType,
  isNoContent,
  onPreviewableArticleClick,
  isPreviewable,
  resetPreviewArticleId,
  previewArticleId,
  subHeaderTitle,
  hideSubHeaderRoutes,
  openArticlePreviewOnClick,
  setOpenArticlePreviewOnClick,
  isOnboardingCompleted,
  articleTypeFilter,
  setArticleTypeFilter,
}: FeedLeftContainerProps): React.ReactElement => {
  const { filters } = useFeedFilters();
  const { data: userPermissions } = useFetchUserPermissions();

  const [displayType, setDisplayType] = useDisplayType();
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const [articleCount, setArticleCount] = useState<number>(FEED_SECTION_PAGINATION.defaultOption);
  const [page, setPage] = useState(1);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const validFilters = Object.values(ContentTypeFilter);

  const subHeaderRoutes = useMemo(
    () =>
      !hideSubHeaderRoutes
        ? getSubHeaderRoutes(userPermissions).map((route) => ({
            ...route,
            to: `${route.to}?type=${articleTypeFilter}`,
          }))
        : [],
    [hideSubHeaderRoutes, articleTypeFilter, userPermissions]
  );

  useEffect(() => {
    setPage(1);
  }, [feedType, filters, articleCount, articleTypeFilter]);

  useEffect(() => {
    if (!validFilters.includes(articleTypeFilter)) {
      navigate(`?type=${ContentTypeFilter.ALL}`);
    }
  }, [articleTypeFilter]);

  const onFeedsRouteClick = () => {
    resetPreviewArticleId();
  };

  const onPersonalizeClick = () => {
    if (isOnboardingCompleted) {
      navigate(`${ROUTES.myContent}/${MY_CONTENT_ROUTES.managePreferences}`);
    } else {
      navigateToAnotherMfe(EXTERNAL_ROUTES.onboarding);
    }
  };

  const renderSubHeaderContent = () => {
    if (isNoContent) {
      return null;
    }
    return (
      <>
        {isDesktop && (
          <Checkbox
            className={cx('article-preview-toggle')}
            dataTestId="article-preview-toggle"
            onClick={() => setOpenArticlePreviewOnClick(!openArticlePreviewOnClick)}
            checked={openArticlePreviewOnClick}
            toggle
            label="Show article previews"
            aria-label="Show article previews"
          />
        )}
        <DisplayTypeSelection selected={displayType} onSelect={setDisplayType} />
        {feedType === FeedType.My && (
          <>
            <Divider vertical />
            <PersonalizeMyCSButton />
          </>
        )}
      </>
    );
  };

  const setPaginationParameters = (articleCount: number) => {
    setPage(1);
    setArticleCount(articleCount);
  };

  const renderArticles = () => {
    return isNoContent ? (
      <div className={cx('no-content-wrapper')}>
        <NoContent
          dataTestId="no-content"
          header="Personalize Your CreditSights Experience"
          infoMessage={
            isOnboardingCompleted
              ? 'Try adjusting your preferences and watchlists to tailor the content displayed.'
              : 'Tell us about your interests to tailor the content displayed on your feed and set email preferences.'
          }
          iconName={ICONS.NotOnboarded}
          buttonParams={{
            ariaLabel: 'Personalize',
            title: 'Personalize',
            onClick: onPersonalizeClick,
          }}
        />
      </div>
    ) : (
      <div className={cx('feed-results')}>
        <div className={cx('feed-filters')}>
          <FiltersBar
            key={feedType}
            filterTypes={getFeedFilters(articleTypeFilter, feedType)}
            hiddenFilterTypes={[FilterTypeEnum.topic]}
            transformFilter={transformFilter}
            articleType={articleTypeFilter}
            contentTypeFiltersMap={contentTypeFiltersMap}
          />
        </div>
        {filters && (
          <div className={cx('feed-articles')} data-testid="feed-articles">
            <ContentTabs
              articleType={articleTypeFilter}
              setArticleType={setArticleTypeFilter}
              permissions={userPermissions!.permissions}
            />
            <div className={cx('article-list-body', { 'is-expanded': isExpanded })}>
              <div className={cx('header-container')}>
                {!isExpanded && (
                  <ResultsFilter
                    values={FEED_SECTION_PAGINATION.options}
                    onResultsCountChange={setPaginationParameters}
                    selectedValue={articleCount}
                  />
                )}
              </div>
              <div className={cx('content', { 'is-expanded': isExpanded })}>
                <ArticleList
                  viewConfig={viewConfig(
                    onPreviewableArticleClick,
                    isPreviewable,
                    userPermissions!,
                    articleTypeFilter === ContentTypeFilter.ALL
                  )}
                  feedType={feedType}
                  expanded={displayType === DisplayType.Expanded}
                  filters={filters}
                  articleCount={articleCount}
                  previewArticleId={previewArticleId}
                  setHasResults={(hasResults) => setIsExpanded(!hasResults)}
                  page={page}
                  setPage={setPage}
                  contentTypeFilter={articleTypeFilter}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cx('body')}>
      <SubHeader
        title={subHeaderTitle || 'Feeds'}
        routes={subHeaderRoutes}
        onRouteClick={onFeedsRouteClick}
        showBottomBorder={!isNoContent}
      >
        {renderSubHeaderContent()}
      </SubHeader>
      {renderArticles()}
    </div>
  );
};

export default FeedLeftContainer;
